import { Fragment, ReactNode } from 'react'

/**
 * Renders the small Markdown subset the support assistant answers with
 * (paragraphs, headings, ordered/unordered lists, bold, italic, inline code and
 * links) as React elements. Nothing is injected as HTML, and only http(s) links
 * become anchors.
 */
const renderMarkdown = (source: string): ReactNode => {
  const blocks = parseBlocks(source.replace(/\r\n?/g, '\n'))
  return blocks.map((block, index) => {
    switch (block.type) {
      case 'heading':
        return <strong key={index}>{renderInline(block.text)}</strong>
      case 'ol':
      case 'ul': {
        const List = block.type
        return (
          <List key={index}>
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>{renderInline(item)}</li>
            ))}
          </List>
        )
      }
      default:
        return (
          <p key={index}>
            {block.lines.map((line, lineIndex) => (
              <Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {renderInline(line)}
              </Fragment>
            ))}
          </p>
        )
    }
  })
}

const ORDERED_ITEM = /^\s*\d+[.)]\s+(.*)$/
const UNORDERED_ITEM = /^\s*[-*+]\s+(.*)$/
const HEADING = /^\s*#{1,6}\s+(.*)$/

const parseBlocks = (source: string) => {
  const blocks: Block[] = []

  source.split('\n').forEach((line) => {
    const last = blocks[blocks.length - 1]

    if (!line.trim()) {
      blocks.push({ type: 'break' })
      return
    }

    const heading = HEADING.exec(line)
    if (heading) {
      blocks.push({ type: 'heading', text: heading[1] })
      return
    }

    const ordered = ORDERED_ITEM.exec(line)
    const unordered = !ordered && UNORDERED_ITEM.exec(line)
    const item = ordered || unordered
    if (item) {
      const type = ordered ? 'ol' : 'ul'
      if (last?.type === type) {
        last.items.push(item[1])
      } else {
        blocks.push({ type, items: [item[1]] })
      }
      return
    }

    // An indented line directly under a list item continues that item
    if ((last?.type === 'ol' || last?.type === 'ul') && /^\s+/.test(line)) {
      last.items[last.items.length - 1] += ` ${line.trim()}`
      return
    }

    if (last?.type === 'p') {
      last.lines.push(line)
    } else {
      blocks.push({ type: 'p', lines: [line] })
    }
  })

  return blocks.filter(
    (block): block is Exclude<Block, { type: 'break' }> =>
      block.type !== 'break'
  )
}

const INLINE =
  /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|`([^`]+)`|\*([^*\s][^*]*)\*|_([^_\s][^_]*)_|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])/g

const renderInline = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = []
  let cursor = 0

  for (const match of Array.from(text.matchAll(INLINE))) {
    const index = match.index ?? 0
    if (index > cursor) {
      nodes.push(text.slice(cursor, index))
    }

    const [
      whole,
      linkText,
      linkUrl,
      bold1,
      bold2,
      code,
      italic1,
      italic2,
      url,
    ] = match
    const key = nodes.length

    if (linkText !== undefined) {
      nodes.push(
        isSafeUrl(linkUrl) ? (
          <ExternalLink key={key} href={linkUrl}>
            {renderInline(linkText)}
          </ExternalLink>
        ) : (
          linkText
        )
      )
    } else if (bold1 !== undefined || bold2 !== undefined) {
      nodes.push(<strong key={key}>{renderInline(bold1 ?? bold2)}</strong>)
    } else if (code !== undefined) {
      nodes.push(<code key={key}>{code}</code>)
    } else if (italic1 !== undefined || italic2 !== undefined) {
      nodes.push(<em key={key}>{renderInline(italic1 ?? italic2)}</em>)
    } else if (url !== undefined) {
      nodes.push(
        <ExternalLink key={key} href={url}>
          {url}
        </ExternalLink>
      )
    } else {
      nodes.push(whole)
    }

    cursor = index + whole.length
  }

  if (cursor < text.length) {
    nodes.push(text.slice(cursor))
  }
  return nodes
}

const isSafeUrl = (url: string) => {
  try {
    return ['http:', 'https:'].includes(new URL(url).protocol)
  } catch {
    return false
  }
}

const ExternalLink = ({
  href,
  children,
}: {
  href: string
  children: ReactNode
}) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
)

type Block =
  | { type: 'p'; lines: string[] }
  | { type: 'heading'; text: string }
  | { type: 'ol' | 'ul'; items: string[] }
  | { type: 'break' }

export { isSafeUrl, renderMarkdown }
